import React, { useEffect, useState, useMemo } from 'react';
import { httpClient } from '../utils/dataProvider'
import { Grid, Typography, Select, MenuItem, FormControl, InputLabel, TextField, Box, CircularProgress, Checkbox, ListItemText } from '@material-ui/core';
import { ResponsiveContainer, BarChart as RBarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts';

import { PageHeader, Surface, MetricCard } from '../components/ui';

// Recharts-based BarChart component (keeps prior props/signature)
const BarChart = ({ data, height = 180, barColor = '#3f51b5', dynamicScale = false }) => {
    if (!data || !data.length) return <Typography variant="body2">No data</Typography>;
    const values = data.map(d => d.value);
    const rawMin = Math.min(...values);
    const rawMax = Math.max(...values) || 1;
    const baseMin = dynamicScale ? rawMin : 0;
    const span = Math.max(1, rawMax - baseMin);
    const pad = span * 0.05; // 5% headroom
    const yDomain = [Math.floor(baseMin - (dynamicScale ? pad : 0)), Math.ceil(rawMax + pad)];

    return (
        <div style={{ width: '100%', height }}>
            <ResponsiveContainer width="100%" height="100%">
                <RBarChart data={data} margin={{ top: 8, right: 12, bottom: 24, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveEnd" angle={-45} textAnchor="end" height={36} />
                    <YAxis domain={yDomain} tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip formatter={(v) => [v, 'Value']} labelFormatter={(l) => l} />
                    <Bar dataKey="value" fill={barColor} radius={[2, 2, 0, 0]} />
                </RBarChart>
            </ResponsiveContainer>
        </div>
    );
};

// Utility: format date to YYYY-MM-DD
const toDayKey = (dateStr) => {
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${day}`;
};

// Utility: format date to YYYY-MM-DD HH (24h)
const toHourKey = (dateStr) => {
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    const day = toDayKey(d.toISOString());
    const hour = `${d.getHours()}`.padStart(2, '0');
    return `${day} ${hour}`;
};

// Utility: format date to YYYY-MM (month key)
const toMonthKey = (dateStr) => {
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, '0');
    return `${y}-${m}`;
};

// Merge logs of same user into rolling sessions where gap between consecutive events <= 60s
const mergeUserLogs = (logs) => {
    const byUser = new Map();
    logs.forEach(l => {
        if (!l.user || !l.user._id) return;
        if (!byUser.has(l.user._id)) byUser.set(l.user._id, []);
        byUser.get(l.user._id).push(l);
    });
    const mergedSessions = [];
    byUser.forEach(userLogs => {
        userLogs.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        let current = null;
        userLogs.forEach(l => {
            const t = new Date(l.createdAt).getTime();
            if (!current) {
                current = { ...l, _sessionStart: t, _lastEvent: t, _events: [l] };
                mergedSessions.push(current);
            } else {
                if (t - current._lastEvent <= 60 * 1000) {
                    // extend rolling window
                    current._lastEvent = t;
                    current._events.push(l);
                } else {
                    current = { ...l, _sessionStart: t, _lastEvent: t, _events: [l] };
                    mergedSessions.push(current);
                }
            }
        });
    });
    return mergedSessions.map(s => ({
        ...s,
        sessionStart: new Date(s._sessionStart),
        sessionEnd: new Date(s._lastEvent),
        sessionLengthSec: Math.max(1, Math.round((s._lastEvent - s._sessionStart) / 1000)),
        eventCount: s._events.length,
        actionCounts: s._events.reduce((acc, e) => { acc[e.action] = (acc[e.action] || 0) + 1; return acc; }, {})
    }));
};

const ACTIONS = [
    'REGISTER',
    'LOGIN',
    'REFRESH',
    'SEARCH',
    'CANCELLED_REQUEST',
    'LOGOUT',
    'REPORT',
    'OTHER'
];

export const LogsList = props => {
    const [rawLogs, setRawLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rangeType, setRangeType] = useState('THIS_MONTH');
    const [granularity, setGranularity] = useState('DAILY');
    const today = useMemo(() => new Date(), []);
    const firstDayOfMonth = useMemo(() => new Date(today.getFullYear(), today.getMonth(), 1), [today]);
    const [customFrom, setCustomFrom] = useState(firstDayOfMonth.toISOString().slice(0, 10));
    const [customTo, setCustomTo] = useState(today.toISOString().slice(0, 10));
    const [showCollapsed, setShowCollapsed] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState([]); // empty => all
    const [selectedActions, setSelectedActions] = useState([]); // empty => all

    const calcRange = () => {
        const end = new Date();
        let start;
        switch (rangeType) {
            case 'LAST_7':
                start = new Date(); start.setDate(end.getDate() - 6); break;
            case 'LAST_30':
                start = new Date(); start.setDate(end.getDate() - 29); break;
            case 'CUSTOM':
                start = new Date(customFrom + 'T00:00:00Z');
                return { start, end: new Date(customTo + 'T23:59:59Z') };
            case 'THIS_MONTH':
            default:
                start = new Date(end.getFullYear(), end.getMonth(), 1); break;
        }
        return { start, end };
    };

    // Fetch logs from backend with server-side date/action filtering (user filter stays client-side)
    const fetchLogs = async (start, end, actions) => {
        setLoading(true); setError(null);
        try {
            const startParam = start.toISOString().slice(0, 10); // YYYY-MM-DD
            const endParam = end.toISOString().slice(0, 10);
            const actionParam = actions && actions.length ? `&action=${encodeURIComponent(actions.join(','))}` : '';
            // Single bounded request instead of a serial page-by-page loop.
            // Backend sorts createdAt desc, so limit=2000 returns the newest
            // 2000 entries in one round trip (same cap as the former 20×100
            // loop) — far faster now the collection is indexed.
            const url = `https://skilzapp.net/api/logs?page=1&limit=2000&startdate=${startParam}&enddate=${endParam}${actionParam}`;
            const { json } = await httpClient(url, { method: 'GET' });
            setRawLogs(json && Array.isArray(json.docs) ? json.docs : []);
        } catch (e) {
            console.error(e);
            setError('Failed to load logs');
        } finally {
            setLoading(false);
        }
    };

    // Re-fetch whenever the effective date range or selectedActions change
    useEffect(() => {
        const { start, end } = calcRange();
        fetchLogs(start, end, selectedActions);
    }, [rangeType, customFrom, customTo, selectedActions]);

    const { start: rangeStart, end: rangeEnd } = calcRange();
    const timeRangeLogs = useMemo(() => {
        if (!rawLogs.length) return [];
        const startMs = rangeStart.getTime();
        const endMs = rangeEnd.getTime();
        return rawLogs.filter(l => {
            const t = new Date(l.createdAt).getTime();
            return !isNaN(t) && t >= startMs && t <= endMs;
        });
    }, [rawLogs, rangeStart, rangeEnd]);

    const filteredLogs = useMemo(() => {
        if (!timeRangeLogs.length) return [];
        return timeRangeLogs.filter(l => {
            const userMatch = selectedUserIds.length === 0 || (l.user && l.user._id ? selectedUserIds.includes(l.user._id) : selectedUserIds.includes('guest'));
            const actionMatch = selectedActions.length === 0 || selectedActions.includes(l.action);
            return userMatch && actionMatch;
        });
    }, [timeRangeLogs, selectedUserIds, selectedActions]);

    const sessions = useMemo(() => mergeUserLogs(filteredLogs), [filteredLogs]);

    const dailyData = useMemo(() => {
        const perDayUsers = new Map();
        const perDaySessions = new Map();
        sessions.forEach(s => {
            if (!s.user || !s.user._id) return;
            const dayKey = toDayKey(s.sessionStart.toISOString());
            if (!dayKey) return;
            if (!perDayUsers.has(dayKey)) perDayUsers.set(dayKey, new Set());
            perDayUsers.get(dayKey).add(s.user._id);
            perDaySessions.set(dayKey, (perDaySessions.get(dayKey) || 0) + 1);
        });
        const days = [];
        for (let d = new Date(rangeStart); d <= rangeEnd; d.setDate(d.getDate() + 1)) {
            const key = toDayKey(d.toISOString());
            const usersSet = perDayUsers.get(key) || new Set();
            const sessionsCount = perDaySessions.get(key) || 0;
            days.push({ date: key, users: usersSet.size, sessions: sessionsCount });
        }
        return days;
    }, [sessions, rangeStart, rangeEnd]);

    const hourlyData = useMemo(() => {
        if (granularity !== 'HOURLY') return [];
        const perHourUsers = new Map();
        const perHourSessions = new Map();
        sessions.forEach(s => {
            if (!s.user || !s.user._id) return;
            const hourKey = toHourKey(s.sessionStart.toISOString());
            if (!hourKey) return;
            if (!perHourUsers.has(hourKey)) perHourUsers.set(hourKey, new Set());
            perHourUsers.get(hourKey).add(s.user._id);
            perHourSessions.set(hourKey, (perHourSessions.get(hourKey) || 0) + 1);
        });
        const hours = [];
        let cursor = new Date(rangeStart.getTime());
        cursor.setMinutes(0, 0, 0);
        let count = 0;
        while (cursor <= rangeEnd && count < 1000) {
            const key = toHourKey(cursor.toISOString());
            const usersSet = perHourUsers.get(key) || new Set();
            const sessionsCount = perHourSessions.get(key) || 0;
            hours.push({ hour: key, users: usersSet.size, sessions: sessionsCount });
            cursor = new Date(cursor.getTime() + 60 * 60 * 1000);
            count++;
        }
        return hours;
    }, [sessions, rangeStart, rangeEnd, granularity]);

    const monthlyData = useMemo(() => {
        if (granularity !== 'MONTHLY') return [];
        const perMonthUsers = new Map();
        const perMonthSessions = new Map();
        sessions.forEach(s => {
            if (!s.user || !s.user._id) return;
            const monthKey = toMonthKey(s.sessionStart.toISOString());
            if (!monthKey) return;
            if (!perMonthUsers.has(monthKey)) perMonthUsers.set(monthKey, new Set());
            perMonthUsers.get(monthKey).add(s.user._id);
            perMonthSessions.set(monthKey, (perMonthSessions.get(monthKey) || 0) + 1);
        });
        const months = [];
        const cursor = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
        const endCursor = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), 1);
        let count = 0;
        while (cursor <= endCursor && count < 240) { // cap ~20 years
            const key = toMonthKey(cursor.toISOString());
            const usersSet = perMonthUsers.get(key) || new Set();
            const sessionsCount = perMonthSessions.get(key) || 0;
            months.push({ month: key, users: usersSet.size, sessions: sessionsCount });
            cursor.setMonth(cursor.getMonth() + 1);
            count++;
        }
        return months;
    }, [sessions, rangeStart, rangeEnd, granularity]);

    const totalActiveUsers = useMemo(() => {
        const u = new Set();
        sessions.forEach(s => { if (s.user && s.user._id) u.add(s.user._id); });
        return u.size;
    }, [sessions]);

    const avgDailyActiveUsers = useMemo(() => {
        if (!dailyData.length) return 0;
        const sum = dailyData.reduce((acc, d) => acc + d.users, 0);
        return (sum / dailyData.length).toFixed(1);
    }, [dailyData]);

    const peakDailyActiveUsers = useMemo(() => dailyData.reduce((m, d) => Math.max(m, d.users), 0), [dailyData]);
    const totalSessions = sessions.length;

    const avgSessionLength = useMemo(() => {
        if (!sessions.length) return 0;
        return (sessions.reduce((a, s) => a + s.sessionLengthSec, 0) / sessions.length / 60).toFixed(2); // minutes
    }, [sessions]);

    const topActions = useMemo(() => {
        const counter = {};
        filteredLogs.forEach(l => { counter[l.action] = (counter[l.action] || 0) + 1; });
        return Object.entries(counter).sort((a, b) => b[1] - a[1]).slice(0, 6);
    }, [filteredLogs]);

    // ── New KPIs ────────────────────────────────────
    const bounceRate = useMemo(() => {
        if (!sessions.length) return '0%';
        const single = sessions.filter(s => s.eventCount === 1).length;
        return `${((single / sessions.length) * 100).toFixed(1)}%`;
    }, [sessions]);

    const eventsPerSession = useMemo(() => {
        if (!sessions.length) return '0';
        return (filteredLogs.length / sessions.length).toFixed(1);
    }, [filteredLogs, sessions]);

    const peakHour = useMemo(() => {
        const hours = {};
        sessions.forEach(s => {
            const h = s.sessionStart.getHours();
            hours[h] = (hours[h] || 0) + 1;
        });
        const sorted = Object.entries(hours).sort((a, b) => b[1] - a[1]);
        if (!sorted.length) return '—';
        const h = parseInt(sorted[0][0]);
        return `${h.toString().padStart(2, '0')}:00–${((h + 1) % 24).toString().padStart(2, '0')}:00`;
    }, [sessions]);

    const retentionRate = useMemo(() => {
        if (dailyData.length < 2 || !totalActiveUsers) return '—';
        const firstDay = dailyData[0];
        const lastDay = dailyData[dailyData.length - 1];
        if (!firstDay.users) return '—';
        return `${Math.min(100, ((lastDay.users / firstDay.users) * 100)).toFixed(0)}%`;
    }, [dailyData, totalActiveUsers]);

    const chartUsers = useMemo(() => {
        if (granularity === 'HOURLY') {
            return hourlyData.map(h => ({ key: h.hour, label: h.hour.slice(5) + ':00', value: h.users }));
        }
        if (granularity === 'MONTHLY') {
            return monthlyData.map(m => ({ key: m.month, label: m.month, value: m.users }));
        }
        return dailyData.map(d => ({ key: d.date, label: d.date.slice(5), value: d.users }));
    }, [granularity, hourlyData, dailyData, monthlyData]);

    const chartSessions = useMemo(() => {
        if (granularity === 'HOURLY') {
            return hourlyData.map(h => ({ key: h.hour, label: h.hour.slice(5) + ':00', value: h.sessions }));
        }
        if (granularity === 'MONTHLY') {
            return monthlyData.map(m => ({ key: m.month, label: m.month, value: m.sessions }));
        }
        return dailyData.map(d => ({ key: d.date, label: d.date.slice(5), value: d.sessions }));
    }, [granularity, hourlyData, dailyData, monthlyData]);

    // Collapsed log view merging duplicates within 60s per user+action for readability
    const collapsedLogs = useMemo(() => {
        if (!showCollapsed) return [];
        const result = [];
        const lastKey = new Map(); // key => {time, count, log}
        filteredLogs
            .slice()
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // newest first
            .forEach(l => {
                const key = (l.user && l.user._id ? l.user._id : 'guest') + '|' + l.action;
                const t = new Date(l.createdAt).getTime();
                if (!lastKey.has(key)) {
                    lastKey.set(key, { time: t, count: 1, log: l });
                    result.push({ ...l, _count: 1 });
                } else {
                    const ref = lastKey.get(key);
                    if (ref.time - t <= 60 * 1000) {
                        ref.count += 1;
                        ref.time = t; // extend backward window
                        // update first occurrence count
                        const idx = result.findIndex(r => r === ref.log || (r.user === ref.log.user && r.createdAt === ref.log.createdAt));
                        if (idx >= 0) result[idx]._count = ref.count;
                    } else {
                        lastKey.set(key, { time: t, count: 1, log: l });
                        result.push({ ...l, _count: 1 });
                    }
                }
            });
        return result;
    }, [filteredLogs, showCollapsed]);

    const users = useMemo(() => {
        const map = new Map();
        rawLogs.forEach(l => { if (l.user && l.user._id) { map.set(l.user._id, l.user.name || l.user._id); } });
        const arr = Array.from(map.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
        // add guest if present
        if (rawLogs.some(l => !l.user || !l.user._id)) arr.unshift({ id: 'guest', name: 'Guest User' });
        return arr;
    }, [rawLogs]);

    return (
        <main className="sk-page sk-logs">
            <PageHeader eyebrow="Insights" title="Activity Logs" description="Analyze active users, sessions, actions, and raw audit events." />

            <div className="sk-logs-filters">
                <Grid item xs={12} md={4} lg={3}>
                    <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel id="range-type-label">Range</InputLabel>
                        <Select labelId="range-type-label" label="Range" value={rangeType} onChange={e => setRangeType(e.target.value)}>
                            <MenuItem value="THIS_MONTH">This Month</MenuItem>
                            <MenuItem value="LAST_7">Last 7 Days</MenuItem>
                            <MenuItem value="LAST_30">Last 30 Days</MenuItem>
                            <MenuItem value="CUSTOM">Custom</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                {rangeType === 'CUSTOM' && (
                    <>
                        <Grid item xs={6} md={2}>
                            <TextField label="From" type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth variant="outlined" size="small" />
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <TextField label="To" type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth variant="outlined" size="small" />
                        </Grid>
                    </>
                )}

                <Grid item xs={12} md={6} lg={3}>
                    <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel id="user-filter-label">Users</InputLabel>
                        <Select
                            labelId="user-filter-label"
                            multiple
                            label="Users"
                            value={selectedUserIds}
                            onChange={e => {
                                const value = e.target.value;
                                if (value.includes('__ALL__')) {
                                    setSelectedUserIds([]); // reset to all
                                } else {
                                    setSelectedUserIds(value);
                                }
                            }}
                            renderValue={(selected) => {
                                if (!selected.length) return 'All Users';
                                const names = selected.map(id => id === 'guest' ? 'Guest User' : (users.find(u => u.id === id)?.name || id));
                                return names.slice(0, 3).join(', ') + (names.length > 3 ? ` +${names.length - 3}` : '');
                            }}
                        >
                            <MenuItem value="__ALL__" dense>
                                <em>All Users</em>
                            </MenuItem>
                            {users.map(u => (
                                <MenuItem key={u.id} value={u.id} dense>
                                    <Checkbox size="small" color="primary" checked={selectedUserIds.indexOf(u.id) > -1} />
                                    <ListItemText primary={u.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                    <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel id="action-filter-label">Actions</InputLabel>
                        <Select
                            labelId="action-filter-label"
                            multiple
                            label="Actions"
                            value={selectedActions}
                            onChange={e => {
                                const value = e.target.value;
                                if (value.includes('__ALL__')) {
                                    setSelectedActions([]);
                                } else {
                                    setSelectedActions(value);
                                }
                            }}
                            renderValue={(selected) => {
                                if (!selected.length) return 'All Actions';
                                return selected.slice(0, 4).join(', ') + (selected.length > 4 ? ` +${selected.length - 4}` : '');
                            }}
                        >
                            <MenuItem value="__ALL__" dense>
                                <em>All Actions</em>
                            </MenuItem>
                            {ACTIONS.map(a => (
                                <MenuItem key={a} value={a} dense>
                                    <Checkbox size="small" color="primary" checked={selectedActions.indexOf(a) > -1} />
                                    <ListItemText primary={a} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </div>

            {loading && (
                <Box display="flex" alignItems="center" mb={2}><CircularProgress size={20} style={{ marginRight: 8 }} /> <Typography variant="body2">Loading logs…</Typography></Box>
            )}
            {error && <Typography color="error" variant="body2" gutterBottom>{error}</Typography>}

            <div className="sk-metric-grid">
                <MetricCard label="Active Users" value={totalActiveUsers} detail="Distinct users in range" />
                <MetricCard tone="blue" label="Avg DAU" value={avgDailyActiveUsers} detail="Average daily active users" />
                <MetricCard tone="purple" label="Peak DAU" value={peakDailyActiveUsers} detail="Highest daily active users" />
                <MetricCard tone="orange" label="Avg Session" value={`${avgSessionLength} min`} detail="Average session duration" />
                <MetricCard label="Total Sessions" value={totalSessions} detail="Merged within 1 minute" />
                <MetricCard tone="blue" label="Events/Session" value={eventsPerSession} detail="Average events per session" />
                <MetricCard tone="purple" label="Bounce Rate" value={bounceRate} detail="Single-event sessions" />
                <MetricCard tone="orange" label="Peak Hour" value={peakHour} detail="Most sessions by hour" />
                <MetricCard label="Retention" value={retentionRate} detail="Last-day versus first-day users" />
            </div>

            <Surface title="Top Actions" description="Most frequent actions in the selected range.">
                <div className="sk-top-actions-grid">
                    {topActions.map(([act, cnt]) => (
                        <div className="sk-top-action-item" key={act}>
                            <span className="sk-top-action-label">{act}</span>
                            <strong className="sk-top-action-value">{cnt}</strong>
                        </div>
                    ))}
                    {!topActions.length && (
                        <div className="sk-top-action-item">
                            <span className="sk-top-action-label">No actions</span>
                            <strong className="sk-top-action-value">—</strong>
                        </div>
                    )}
                </div>
            </Surface>
            <div className="sk-logs-controls" style={{ marginTop: 16 }}>
                <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                    <InputLabel id="granularity-label">Granularity</InputLabel>
                    <Select labelId="granularity-label" label="Granularity" value={granularity} onChange={e => setGranularity(e.target.value)}>
                        <MenuItem value="DAILY">Daily</MenuItem>
                        <MenuItem value="MONTHLY">Monthly</MenuItem>
                        <MenuItem value="HOURLY">Hourly</MenuItem>
                    </Select>
                </FormControl>
            </div>

            <div className="sk-chart-row">
                <Surface title={granularity === 'HOURLY' ? 'Hourly Active Users' : granularity === 'MONTHLY' ? 'Monthly Active Users' : 'Daily Active Users'}
                         description={`Distinct users per ${granularity === 'HOURLY' ? 'hour' : granularity === 'MONTHLY' ? 'month' : 'day'}`}>
                    <BarChart data={chartUsers} barColor="#3f51b5" dynamicScale={granularity === 'HOURLY'} />
                </Surface>
                <Surface title={granularity === 'HOURLY' ? 'Hourly Sessions' : granularity === 'MONTHLY' ? 'Monthly Sessions' : 'Daily Sessions'}
                         description="Merged within 1 minute">
                    <BarChart data={chartSessions} barColor="#009688" dynamicScale={granularity === 'HOURLY'} />
                </Surface>
            </div>

            <Surface title={showCollapsed ? 'Collapsed Logs (60s merge)' : 'Recent Logs'}
                     actions={
                        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                            <InputLabel id="collapsed-label">View</InputLabel>
                            <Select labelId="collapsed-label" label="View" value={showCollapsed ? 'COLLAPSED' : 'RAW'} onChange={e => setShowCollapsed(e.target.value === 'COLLAPSED')}>
                                <MenuItem value="RAW">Raw Logs</MenuItem>
                                <MenuItem value="COLLAPSED">Collapsed (60s)</MenuItem>
                            </Select>
                        </FormControl>
                     }>
                <div className="sk-log-table">
                    {(showCollapsed ? collapsedLogs : filteredLogs).slice(0, 200).map((log, i) => (
                        <div key={i} className="sk-log-entry">
                            <div className="sk-log-entry-main">
                                <span className="sk-log-user">{log.user ? log.user.name : 'Guest'}</span>
                                <span className="sk-log-action">{log.action}</span>
                                {log._count && log._count > 1 && <span className="sk-log-badge">×{log._count}</span>}
                            </div>
                            <div className="sk-log-entry-meta">
                                <span className="sk-meta sk-meta-dim">{new Date(log.createdAt).toLocaleString()}</span>
                                {log.description && <span className="sk-meta">{log.description}</span>}
                                {log.relatedType && log.relatedId ? (
                                    <a href={`/#/${log.relatedType}/${log.relatedId}/show`} className="sk-link-chip">View related</a>
                                ) : null}
                            </div>
                        </div>
                    ))}
                    {!(showCollapsed ? collapsedLogs : filteredLogs).length && !loading && <p className="sk-muted" style={{ padding: 16 }}>No logs in range.</p>}
                </div>
            </Surface>
        </main>
    );
};

